import React, { useState } from "react";
import { Table, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "../../../../helpers/icons/fontAwesomeIcons";
import PropTypes from "prop-types";
import classes from "./FeatureTable.module.scss";
import MoreInfoButton from "./MoreInfoButton/MoreInfoButton";
import FeatureMoreInfoModal from "./FeatureMoreInfoModal/FeatureMoreInfoModal";

const FeatureTable = ({
  t,
  data = [], // Provide default empty array
  columns = [],
  tableType = "",
  loading = false,
  actions = {
    view: false,
    edit: false,
    delete: false,
    custom: [], // Ensure custom is initialized as empty array
  },
  onActionClick = () => {},
  onRowClick = null,
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Ensure actions.custom is always an array
  const customActions = actions.custom || [];

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to data (safely handle if data is undefined)
  const sortedData = React.useMemo(() => {
    if (!data || !sortConfig.key) return data || [];

    return [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  // Get sort icon based on current sort state
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FontAwesomeIcon icon={faSort} />;
    if (sortConfig.direction === "asc")
      return <FontAwesomeIcon icon={faSortUp} />;
    return <FontAwesomeIcon icon={faSortDown} />;
  };

  // VIEW MORE RELATED FUNCTIONS

  // Function to handle viewing record details
  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setShowDetailsModal(true);
  };

  const handleActionClick = (actionType, record) => {
    if (actionType === "view") {
      // Handle view action internally with the generic details modal
      handleViewRecord(record);
    } else {
      // Bubble up other actions to the parent
      onActionClick(actionType, record);
    }
  };

  return (
    <div className={classes.tableContainer}>
      {loading ? (
        <div className={classes.loaderContainer}>
          <Spinner animation="border" role="status">
            <span className="sr-only">{t("loading")}</span>
          </Spinner>
        </div>
      ) : (
        <>
          {!data || data.length === 0 ? (
            <div className={classes.emptyState}>
              <p>{t("noData")}</p>
            </div>
          ) : (
            <>
              <Table className={classes.featureTable}>
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        onClick={
                          column.sortable
                            ? () => handleSort(column.key)
                            : undefined
                        }
                        className={`
                          ${column.sortable ? classes.sortableHeader : ""}
                          ${
                            tableType
                              ? `table-${tableType}-col-${column.key} table-${tableType}-col-${column.key}-header`
                              : ""
                          }
                        `}
                      >
                        {column.label}
                        {column.sortable && (
                          <span className={classes.sortIcon}>
                            {getSortIcon(column.key)}
                          </span>
                        )}
                      </th>
                    ))}
                    {(actions.view ||
                      actions.edit ||
                      actions.delete ||
                      customActions.length > 0) && (
                      <th className={classes.actionsColumn}>{""}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, rowIndex) => (
                    <tr
                      key={row.id || rowIndex}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={onRowClick ? classes.clickableRow : ""}
                    >
                      {columns.map((column) => (
                        <td
                          key={`${row.id || rowIndex}-${column.key}`}
                          className={
                            tableType
                              ? `table-${tableType}-col-${column.key} table-${tableType}-col-${column.key}-data`
                              : ""
                          }
                        >
                          {column.render ? column.render(row) : row[column.key]}
                        </td>
                      ))}
                      {(actions.view ||
                        actions.edit ||
                        actions.delete ||
                        customActions.length > 0) && (
                        <td className={classes.actionsCell}>
                          <MoreInfoButton
                            t={t}
                            row={row}
                            onActionClick={handleActionClick}
                            actions={actions}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <FeatureMoreInfoModal
                  t={t}
                  show={showDetailsModal}
                  onClose={() => setShowDetailsModal(false)}
                  data={selectedRecord}
                  columns={columns}
                  tableType={tableType}
                />
              </Table>
            </>
          )}
        </>
      )}
    </div>
  );
};

FeatureTable.propTypes = {
  t: PropTypes.func.isRequired,
  data: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      render: PropTypes.func,
    })
  ),
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    recordsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
  }),
  actions: PropTypes.shape({
    view: PropTypes.bool,
    edit: PropTypes.bool,
    delete: PropTypes.bool,
    custom: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        icon: PropTypes.object.isRequired, // FontAwesomeIcon
      })
    ),
  }),
  onActionClick: PropTypes.func,
  onRowClick: PropTypes.func,
};

export default FeatureTable;
